import { useState, useMemo, useRef } from "react";
import { useAuth } from "@clerk/react";
import { useLocation } from "wouter";
import AdminLayout from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Plus, Pencil, Trash2, RefreshCw, LayoutTemplate, Send, Search, Loader2, Crown, User, Paperclip, X } from "lucide-react";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

interface EmailTemplate {
  id: number;
  name: string;
  subject: string;
  htmlBody: string;
  createdAt: string;
  updatedAt: string;
}

interface MemberRow {
  id: number;
  email: string;
  displayName: string | null;
  membershipTier: string;
}

export default function AdminEmailTemplatesPage() {
  const { getToken } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();

  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<EmailTemplate | null>(null);

  // Dialog state
  const [sendTarget, setSendTarget] = useState<EmailTemplate | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [tierFilter, setTierFilter] = useState<"all" | "free" | "paid">("all");
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState<{ sent: number; errors: number; total: number } | null>(null);

  // Attachment state
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const attachInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery<{ templates: EmailTemplate[] }>({
    queryKey: ["admin-email-templates"],
    queryFn: async () => {
      const token = await getToken();
      const res = await fetch("/api/admin/email-templates", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
      return res.json();
    },
  });

  const { data: membersData, isLoading: membersLoading } = useQuery<MemberRow[]>({
    queryKey: ["admin-members"],
    enabled: !!sendTarget,
    queryFn: async () => {
      const token = await getToken();
      const res = await fetch("/api/admin/members", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
      return res.json();
    },
  });

  const allMembers = membersData ?? [];

  const filteredMembers = useMemo(() => {
    return allMembers.filter(m => {
      const matchTier = tierFilter === "all" || m.membershipTier === tierFilter;
      const q = searchTerm.trim().toLowerCase();
      const matchSearch = !q
        || m.email.toLowerCase().includes(q)
        || (m.displayName?.toLowerCase().includes(q) ?? false);
      return matchTier && matchSearch;
    });
  }, [allMembers, tierFilter, searchTerm]);

  const templates = data?.templates ?? [];

  function openSendDialog(t: EmailTemplate) {
    setSendTarget(t);
    setSelectedIds(new Set());
    setSearchTerm("");
    setTierFilter("all");
    setAttachmentFile(null);
  }

  function closeSendDialog() {
    if (sending) return;
    setSendTarget(null);
    setAttachmentFile(null);
  }

  function toggleMember(id: number) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (filteredMembers.every(m => selectedIds.has(m.id))) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredMembers.forEach(m => next.delete(m.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredMembers.forEach(m => next.add(m.id));
        return next;
      });
    }
  }

  const allFilteredSelected = filteredMembers.length > 0 && filteredMembers.every(m => selectedIds.has(m.id));

  async function handleSend() {
    if (!sendTarget || selectedIds.size === 0) return;
    setSending(true);
    setSendProgress({ sent: 0, errors: 0, total: selectedIds.size });
    try {
      const token = await getToken();

      // Encode optional PDF attachment as base64
      let attachmentPayload: { filename: string; content: string } | undefined;
      if (attachmentFile) {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = e => resolve((e.target?.result as string).split(",")[1] ?? "");
          reader.onerror = reject;
          reader.readAsDataURL(attachmentFile);
        });
        attachmentPayload = { filename: attachmentFile.name, content: base64 };
      }

      const res = await fetch(`/api/admin/email-templates/${sendTarget.id}/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ memberIds: Array.from(selectedIds), attachment: attachmentPayload }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? "發送失敗");
      }
      if (!res.body) throw new Error("無法讀取伺服器回應");

      // 後端以 SSE 串流即時進度，逐段解析 "data: {...}" 事件
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalResult: { sent: number; errors: number } | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() ?? "";
        for (const chunk of chunks) {
          const line = chunk.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const evt = JSON.parse(line.slice(5).trim());
            if (evt.type === "progress" || evt.type === "done") {
              setSendProgress({ sent: evt.sent, errors: evt.errors, total: evt.total });
            }
            if (evt.type === "done") {
              finalResult = { sent: evt.sent, errors: evt.errors };
            }
          } catch {
            // 忽略無法解析的片段
          }
        }
      }

      if (!finalResult) throw new Error("發送失敗（連線中斷）");
      toast({
        title: "電郵已發送",
        description: `成功發送 ${finalResult.sent} 封${finalResult.errors ? `，${finalResult.errors} 封失敗` : ""}`,
      });
      setSendTarget(null);
    } catch (e: any) {
      toast({ title: "發送失敗", description: e.message, variant: "destructive" });
    } finally {
      setSending(false);
      setSendProgress(null);
    }
  }

  async function doDelete(t: EmailTemplate) {
    setDeleteConfirm(null);
    setDeletingId(t.id);
    try {
      const token = await getToken();
      const res = await fetch(`/api/admin/email-templates/${t.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? "刪除失敗");
      toast({ title: "範本已刪除", description: `「${t.name}」已成功刪除` });
      qc.invalidateQueries({ queryKey: ["admin-email-templates"] });
    } catch (e: any) {
      toast({ title: "刪除失敗", description: e.message, variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  }

  function formatDate(iso: string) {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("zh-HK", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  }

  return (
    <AdminLayout tab="email-templates">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">電郵範本</h2>
            <p className="text-sm text-muted-foreground">設計並儲存電郵範本，可選擇多個會員發送</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => qc.invalidateQueries({ queryKey: ["admin-email-templates"] })}
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> 刷新
            </Button>
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() => navigate("/admin/email-templates/new")}
            >
              <Plus className="h-4 w-4" /> 新增範本
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-20 rounded-lg" />
            ))}
          </div>
        ) : templates.length === 0 ? (
          <div className="border rounded-lg p-12 text-center text-muted-foreground">
            <LayoutTemplate className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium mb-1">尚無電郵範本</p>
            <p className="text-xs mb-4">點擊「新增範本」開始建立你的第一個電郵範本</p>
            <Button size="sm" onClick={() => navigate("/admin/email-templates/new")}>
              <Plus className="h-4 w-4 mr-1.5" /> 新增範本
            </Button>
          </div>
        ) : (
          <div className="border rounded-lg divide-y bg-card">
            {templates.map(t => (
              <div key={t.id} className="flex items-start gap-4 px-4 py-3">
                <div className="p-1.5 rounded-md shrink-0 bg-violet-50 text-violet-500">
                  <LayoutTemplate className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{t.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 truncate">
                    主題：{t.subject}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    更新：{formatDate(t.updatedAt)}
                  </div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs gap-1.5 h-8 text-violet-600 border-violet-300 hover:bg-violet-50 hover:text-violet-700"
                    onClick={() => openSendDialog(t)}
                  >
                    <Send className="h-3.5 w-3.5" /> 發送
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs gap-1.5 h-8"
                    onClick={() => navigate(`/admin/email-templates/${t.id}/edit`)}
                  >
                    <Pencil className="h-3.5 w-3.5" /> 編輯
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs gap-1.5 h-8 text-destructive border-destructive/30 hover:bg-destructive/5"
                    disabled={deletingId === t.id}
                    onClick={() => setDeleteConfirm(t)}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> 刪除
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 發送給會員 Dialog */}
      <Dialog open={!!sendTarget} onOpenChange={open => { if (!open) closeSendDialog(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>選擇會員發送</DialogTitle>
            <DialogDescription className="truncate">
              範本：{sendTarget?.name}
            </DialogDescription>
          </DialogHeader>

          {/* 篩選工具列 */}
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                className="pl-8 h-8 text-sm"
                placeholder="搜尋電郵 / 姓名…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-1.5">
              {(["all", "free", "paid"] as const).map(tier => (
                <Button
                  key={tier}
                  size="sm"
                  variant={tierFilter === tier ? "default" : "outline"}
                  className="h-7 text-xs"
                  onClick={() => setTierFilter(tier)}
                >
                  {tier === "all" ? "全部" : tier === "paid" ? "付費" : "免費"}
                </Button>
              ))}
              <div className="flex-1" />
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs text-muted-foreground"
                onClick={toggleAll}
                disabled={filteredMembers.length === 0}
              >
                {allFilteredSelected ? "取消全選" : "全選"}
              </Button>
            </div>
          </div>

          {/* 會員列表 */}
          <div className="border rounded-md overflow-hidden">
            {membersLoading ? (
              <div className="space-y-1 p-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 rounded" />
                ))}
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                {allMembers.length === 0 ? "暫無會員" : "沒有符合條件的會員"}
              </div>
            ) : (
              <ScrollArea className="h-64">
                <div className="divide-y">
                  {filteredMembers.map(m => (
                    <label
                      key={m.id}
                      className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                      <Checkbox
                        checked={selectedIds.has(m.id)}
                        onCheckedChange={() => toggleMember(m.id)}
                      />
                      <div className={`p-1 rounded shrink-0 ${m.membershipTier === "paid" ? "bg-amber-50 text-amber-500" : "bg-secondary text-muted-foreground"}`}>
                        {m.membershipTier === "paid"
                          ? <Crown className="h-3 w-3" />
                          : <User className="h-3 w-3" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{m.email}</div>
                        {m.displayName && (
                          <div className="text-xs text-muted-foreground truncate">{m.displayName}</div>
                        )}
                      </div>
                      <Badge
                        variant={m.membershipTier === "paid" ? "outline" : "secondary"}
                        className={`text-xs shrink-0 ${m.membershipTier === "paid" ? "bg-amber-50 text-amber-600 border-amber-200" : ""}`}
                      >
                        {m.membershipTier === "paid" ? "付費" : "免費"}
                      </Badge>
                    </label>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>

          {/* 附件 */}
          <div className="border rounded-md p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium flex items-center gap-1.5">
                <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
                加入附件（選填）
              </span>
              <input
                ref={attachInputRef}
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                onChange={e => {
                  const f = e.target.files?.[0] ?? null;
                  setAttachmentFile(f);
                  e.target.value = "";
                }}
              />
              {attachmentFile ? (
                <button
                  type="button"
                  onClick={() => setAttachmentFile(null)}
                  className="flex items-center gap-1 text-xs text-destructive hover:underline"
                >
                  <X className="h-3 w-3" /> 移除
                </button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1.5"
                  onClick={() => attachInputRef.current?.click()}
                >
                  <Paperclip className="h-3 w-3" /> 選擇 PDF
                </Button>
              )}
            </div>
            {attachmentFile ? (
              <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded px-2.5 py-1.5 text-xs text-blue-700">
                <Paperclip className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate font-medium">{attachmentFile.name}</span>
                <span className="text-blue-500 ml-auto shrink-0">
                  {(attachmentFile.size / 1024 / 1024).toFixed(1)} MB
                </span>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                支援 PDF 格式，附件會隨電郵發送給所有選定會員
              </p>
            )}
          </div>

          {sending && sendProgress && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  ({sendProgress.sent + sendProgress.errors}/{sendProgress.total}) 已發送…
                  {sendProgress.errors > 0 && <span className="text-destructive ml-1">{sendProgress.errors} 封失敗</span>}
                </span>
                <span>{Math.round(((sendProgress.sent + sendProgress.errors) / Math.max(sendProgress.total, 1)) * 100)}%</span>
              </div>
              <div className="h-1.5 w-full bg-secondary rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${((sendProgress.sent + sendProgress.errors) / Math.max(sendProgress.total, 1)) * 100}%` }}
                />
              </div>
            </div>
          )}

          <DialogFooter className="items-center gap-3">
            <span className="text-sm text-muted-foreground mr-auto">
              已選 <strong>{selectedIds.size}</strong> 位會員
              {attachmentFile && <span className="ml-1 text-blue-600">· 含附件</span>}
            </span>
            <Button variant="outline" onClick={closeSendDialog} disabled={sending}>
              取消
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || selectedIds.size === 0}
              className="gap-1.5"
            >
              {sending
                ? <><Loader2 className="h-4 w-4 animate-spin" /> 發送中…（{sendProgress ? sendProgress.sent + sendProgress.errors : 0}/{sendProgress?.total ?? selectedIds.size}）</>
                : <><Send className="h-4 w-4" /> 確認發送（{selectedIds.size}）</>
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteConfirm !== null}
        title="刪除電郵範本"
        description={`確定刪除範本「${deleteConfirm?.name}」？此操作不可復原。`}
        confirmLabel="確定刪除"
        destructive
        onConfirm={() => { const t = deleteConfirm!; doDelete(t); }}
        onCancel={() => setDeleteConfirm(null)}
      />
    </AdminLayout>
  );
}
